import { Injectable } from "@angular/core";
import { BaseHttpService } from "./base-http.service";

export interface WeightFactors {
  accessCount: number;
  averageResponseTime: number;
  responseSize: number;
}
export interface CacheEntry<T> {
  timestamp: number;
  data: T;
  accessCount: number;
  averageResponseTime: number;
  responseSize: number;
  weightFactors?: WeightFactors;
}
export type StorageType = "memory" | "local" | "session";
@Injectable({
  providedIn: "root"
})
/* eslint-disable-next-line @tseslint/no-extraneous-class */
export class CacheManager {
  private static cache: Record<string, Map<string, CacheEntry<unknown>>> = {};

  private static storageType: Record<string, StorageType> = {};

  private static totalCacheSize: number = 100;

  private static serviceAccessCount: Record<string, number> = {};

  private static serviceCacheSize: Record<string, number> = {};

  private static weightFactors: Record<string, WeightFactors> = {};

  static register<S extends BaseHttpService<S>>(service: S, storage?: StorageType, weightFactors?: WeightFactors): void {
    const serviceName: string = service.constructor.name;

    this.serviceAccessCount[ serviceName ] = 0;
    this.serviceCacheSize[ serviceName ] = Math.floor(this.totalCacheSize / 2);
    this.storageType[ serviceName ] = storage ?? "memory";
    this.weightFactors[ serviceName ] = weightFactors ?? { accessCount: 1, averageResponseTime: 1, responseSize: 1 };
  }

  static getCache (serviceName: string): Map<string, CacheEntry<unknown>> {
    const storageType: StorageType | undefined = this.storageType[ serviceName ];

    if (storageType === "memory")
      return this.cache[ serviceName ] ?? new Map<string, CacheEntry<unknown>>();

    const storage: Storage = storageType === "session" ? sessionStorage : localStorage,
      item: string | null = storage.getItem(`${ serviceName }Cache`);

    /* eslint-disable-next-line @tseslint/no-unsafe-argument */
    return item ? new Map<string, CacheEntry<unknown>>(JSON.parse(item)) : new Map<string, CacheEntry<unknown>>();
  }

  static setCache<T>(serviceName: string, key: string, entry: CacheEntry<T>): void {
    const storageType: StorageType | undefined = this.storageType[ serviceName ];

    if (storageType === "memory")
      this.getCache(serviceName).set(key, entry);
    else {
      const cache: Map<string, CacheEntry<unknown>> = this.getCache(serviceName),
        storage: Storage = storageType === "session" ? sessionStorage : localStorage;

      cache.set(key, entry);
      storage.setItem(`${ serviceName }Cache`, JSON.stringify([ ...cache ]));
    }
  }

  static setWeightFactors (serviceName: string, factors: { accessCount: number; averageResponseTime: number; responseSize: number; }): void {
    this.weightFactors[ serviceName ] = factors;
  }

  /* eslint-disable-next-line @tseslint/no-unnecessary-type-parameters */
  static get<T>(serviceName: string, key: string): T | null {
    const cache: Map<string, CacheEntry<unknown>> = this.getCache(serviceName),
      entry: CacheEntry<unknown> | undefined = cache.get(key);

    return entry ? entry.data as T : null;
  }

  /* eslint-disable-next-line @tseslint/no-unnecessary-type-parameters, @tseslint/max-params */
  static set<T>(serviceName: string, key: string, data: T, responseTime: number, responseSize: number, weightFactors: WeightFactors): void {
    const serviceCache: Map<string, CacheEntry<unknown>> = this.getCache(serviceName),
      existingEntry: CacheEntry<unknown> | undefined = serviceCache.get(key),
      newEntry: {
        data: T;
        responseSize: number;
        timestamp: number;
        accessCount: number;
        averageResponseTime: number;
        weightFactors: WeightFactors;
      } = {
        data,
        responseSize,
        timestamp: Date.now(),
        accessCount: existingEntry ? ++existingEntry.accessCount : 1,
        averageResponseTime: existingEntry ? (existingEntry.averageResponseTime + responseTime) / 2 : responseTime,
        weightFactors
      };

    this.setCache(serviceName, key, newEntry);

    /* eslint-disable-next-line @tseslint/no-non-null-assertion */
    if (serviceCache.size > this.serviceCacheSize[ serviceName ]!)
      serviceCache[ "delete" ](this.findLeastUsefulEntry(serviceName));
  }

  private static findLeastUsefulEntry (serviceName: string): string {
    const cache: Map<string, CacheEntry<unknown>> = this.getCache(serviceName),
      keys: string[] = [ ...cache.keys() ];

    return keys.reduce((a: string, b: string) => {
      const weightA: number = this.calculateWeight(serviceName, a);
      const weightB: number = this.calculateWeight(serviceName, b);

      return weightA < weightB ? a : b;
    });
  }

  private static calculateWeight (serviceName: string, key: string): number {
    const entry: CacheEntry<unknown> | undefined = this.getCache(serviceName).get(key),
      factors: WeightFactors | undefined = entry?.weightFactors ?? this.weightFactors[ serviceName ];

    if (!entry || !factors) return Infinity;

    return entry.accessCount * factors.accessCount
      * entry.averageResponseTime * factors.averageResponseTime
      * entry.responseSize * factors.responseSize;
  }

  static isExpired (serviceName: string, key: string, maxAge: number): boolean {
    const entry: CacheEntry<unknown> | undefined = this.getCache(serviceName).get(key);

    return !entry || Date.now() - entry.timestamp > maxAge;
  }

  static keyExists (serviceName: string, key: string): boolean {
    return Boolean(this.getCache(serviceName).get(key));
  }
}
